import { useState, useMemo, useCallback } from 'react';
import type { DateRangePreset } from '../types/analytics.types';
import type { EngagementMetricsQueryParams } from '../types/engagement-metrics.types';

type EngagementMetricsFiltersState = {
  dateRange: DateRangePreset;
  startDate: string | null;
  endDate: string | null;
  queryParams: EngagementMetricsQueryParams;
  setDateRange: (preset: DateRangePreset) => void;
  setCustomRange: (start: string, end: string) => void;
  resetFilters: () => void;
};

const DEFAULT_DATE_RANGE: DateRangePreset = '30d';

/**
 * Independent filter state for the Engagement Metrics dashboard.
 * Shaped like useAnalyticsFilters but intentionally a separate instance/hook —
 * Engagement Metrics' filter set (engagement type, initiator role, referral
 * source, etc.) doesn't overlap with Ecosystem Analytics'. Later tickets add
 * those fields here as they're implemented.
 */
export const useEngagementMetricsFilters = (): EngagementMetricsFiltersState => {
  const [dateRange, setDateRangeState] = useState<DateRangePreset>(DEFAULT_DATE_RANGE);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const setDateRange = useCallback((preset: DateRangePreset) => {
    setDateRangeState(preset);
    if (preset !== 'custom') {
      setStartDate(null);
      setEndDate(null);
    }
  }, []);

  const setCustomRange = useCallback((start: string, end: string) => {
    setDateRangeState('custom');
    setStartDate(start);
    setEndDate(end);
  }, []);

  const resetFilters = useCallback(() => {
    setDateRangeState(DEFAULT_DATE_RANGE);
    setStartDate(null);
    setEndDate(null);
  }, []);

  const queryParams = useMemo<EngagementMetricsQueryParams>(() => {
    const params: EngagementMetricsQueryParams = { dateRange };
    if (dateRange === 'custom' && startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    return params;
  }, [dateRange, startDate, endDate]);

  return {
    dateRange,
    startDate,
    endDate,
    queryParams,
    setDateRange,
    setCustomRange,
    resetFilters,
  };
};
